import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { ContactAttempt } from '@/models/restaurant.model';
import { LeadStatus } from '@/models/lead.model';

export interface OutreachRecord {
  id: string;
  leadId: string;
  date: Date;
  method: 'email' | 'phone' | 'in_person' | 'linkedin' | 'other';
  outcome: 'no_response' | 'voicemail' | 'spoke' | 'interested' | 'not_interested' | 'callback_requested';
  notes?: string;
  contactedBy: string;
  createdAt: Date;
}

export class OutreachService {
  /**
   * Record a contact attempt
   */
  static async recordContactAttempt(
    leadId: string,
    attempt: Omit<ContactAttempt, 'date'> & { contactedBy: string }
  ): Promise<string> {
    const outreachRecord = {
      leadId,
      date: Timestamp.now(),
      method: attempt.method,
      outcome: attempt.outcome,
      notes: attempt.notes,
      contactedBy: attempt.contactedBy,
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'outreachRecords'), outreachRecord);

    // Update lead with latest contact info
    const leadRef = doc(db, 'leads', leadId);
    const leadSnap = await getDoc(leadRef);
    
    if (leadSnap.exists()) {
      const leadData = leadSnap.data();
      const contactAttempts = (leadData.contactAttempts || 0) + 1;
      
      // Update lead status based on outcome
      let newStatus: LeadStatus = leadData.status;
      if (attempt.outcome === 'interested') {
        newStatus = 'qualified';
      } else if (attempt.outcome === 'not_interested') {
        newStatus = 'closed-lost';
      } else if (attempt.outcome === 'spoke' && leadData.status === 'new') {
        newStatus = 'contacted';
      }

      await updateDoc(leadRef, {
        contactAttempts,
        lastContactDate: Timestamp.now(),
        status: newStatus,
        updatedAt: Timestamp.now(),
      });
    }

    return docRef.id;
  }

  /**
   * Get outreach history for a lead
   */
  static async getOutreachHistory(leadId: string): Promise<OutreachRecord[]> {
    const q = query(
      collection(db, 'outreachRecords'),
      where('leadId', '==', leadId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as OutreachRecord[];
  }

  /**
   * Get all outreach records for a user
   */
  static async getUserOutreach(userId: string): Promise<OutreachRecord[]> {
    const q = query(
      collection(db, 'outreachRecords'),
      where('contactedBy', '==', userId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as OutreachRecord[];
  }

  /**
   * Get outreach statistics
   */
  static async getOutreachStats(userId?: string): Promise<{
    total: number;
    byMethod: Record<string, number>;
    byOutcome: Record<string, number>;
    last30Days: number;
  }> {
    let q = query(
      collection(db, 'outreachRecords'),
      orderBy('date', 'desc')
    );

    if (userId) {
      q = query(q, where('contactedBy', '==', userId));
    }

    const snapshot = await getDocs(q);
    const records = snapshot.docs.map((doc) => doc.data()) as OutreachRecord[];

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const byMethod: Record<string, number> = {};
    const byOutcome: Record<string, number> = {};
    let last30Days = 0;

    records.forEach((record) => {
      // Count by method
      byMethod[record.method] = (byMethod[record.method] || 0) + 1;

      // Count by outcome
      byOutcome[record.outcome] = (byOutcome[record.outcome] || 0) + 1;

      // Count last 30 days
      if (record.date >= thirtyDaysAgo) {
        last30Days++;
      }
    });

    return {
      total: records.length,
      byMethod,
      byOutcome,
      last30Days,
    };
  }
}

